// =====================================================================
// OrderPhotos.jsx — Commande prête Nespart (13-10-2026) — page PUBLIQUE /order/<token>
// =====================================================================
// Le client (Nespart) ouvre ici, sans compte, les photos de sa commande depuis
// le bouton « View the N photos » du mail « ready for collection ». En ANGLAIS.
// Le jeton du lien EST l'accès (60 jours) ; toute erreur (inconnu, expiré,
// réseau) donne le même écran : « This link has expired or is invalid. »
// Jamais de nom d'opérateur ni d'identifiant interne (le serveur n'en envoie pas).
// La commande est désignée par sa réf. de chargement (ex. BROUT117200), comme dans le
// mail (« Loading reference ») — seul identifiant depuis le 14/10 (`orderRef` de l'API).
// Styles autonomes, comme Embauche.jsx (aucune barre Zeus sur cette page).
// =====================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicReadyOrders } from '../services/api.js';
import { abregeSscc } from './commande-prete/sscc.js';

const wrap = { maxWidth: 1080, margin: '0 auto', padding: '20px 16px 48px', fontFamily: 'system-ui, -apple-system, "Segoe UI", Arial, sans-serif', color: '#17232a', boxSizing: 'border-box' };
const brand = { fontWeight: 800, color: '#0f6e74', fontSize: 15, letterSpacing: '.01em' };

// « 2 pallets and 1 parcel » (singulier / pluriel), comme le sujet du mail.
function unitsText(units) {
  const t = [];
  const p = units?.pallets || 0, c = units?.parcels || 0;
  if (p) t.push(`${p} pallet${p > 1 ? 's' : ''}`);
  if (c) t.push(`${c} parcel${c > 1 ? 's' : ''}`);
  return t.join(' and ');
}
// « 12 December 2026 » (en-GB, heure de Bruxelles), comme dans le mail.
const dateEn = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Brussels' });
const legende = (p) => `${p.unit} · ${p.i}/${p.n}`;

export default function OrderPhotos() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState('loading');   // loading | ok | invalid
  const [ouverte, setOuverte] = useState(null);  // index de la photo en plein écran
  const retour = useRef(null);                   // vignette à refocaliser à la fermeture
  const fermer = useRef(null);

  useEffect(() => {
    let actif = true;
    publicReadyOrders.get(token)
      .then((d) => { if (actif) { setData(d); setEtat('ok'); } })
      .catch(() => { if (actif) setEtat('invalid'); });
    return () => { actif = false; };
  }, [token]);

  // Page client : en anglais, et jamais indexée par un moteur de recherche.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const lang = document.documentElement.lang;
    const titre = document.title;
    document.documentElement.lang = 'en';
    return () => { meta.remove(); document.documentElement.lang = lang; document.title = titre; };
  }, []);
  useEffect(() => {
    if (data) document.title = `${data.orderRef} — photos`;
  }, [data]);

  const photos = data?.photos || [];
  const n = photos.length;
  const aller = useCallback((delta) => setOuverte((i) => (i === null ? i : (i + delta + n) % n)), [n]);
  const fermerVue = useCallback(() => {
    setOuverte(null);
    if (retour.current) retour.current.focus();
  }, []);

  // Plein écran : Échap ferme, ← / → passent d'une photo à l'autre.
  useEffect(() => {
    if (ouverte === null) return undefined;
    fermer.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') fermerVue();
      else if (e.key === 'ArrowRight') aller(1);
      else if (e.key === 'ArrowLeft') aller(-1);
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, [ouverte, aller, fermerVue]);

  if (etat === 'loading') return <div style={wrap}><div style={brand}>Brain E-Log</div><p style={{ color: '#5b6b73' }}>Loading…</p></div>;
  if (etat === 'invalid') {
    return (
      <div style={wrap}>
        <div style={brand}>Brain E-Log</div>
        <div role="alert" style={{ marginTop: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, fontSize: 17 }}>
          This link has expired or is invalid.
        </div>
      </div>
    );
  }

  const total = data.totalPhotos ?? n;
  const courante = ouverte !== null ? photos[ouverte] : null;

  return (
    <div style={wrap}>
      <div style={brand}>Brain E-Log</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '10px 0 16px' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 'clamp(1.25rem, 3.4vw, 1.7rem)', margin: 0, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
            {`Loading reference ${data.orderRef}${unitsText(data.units) ? ` — ${unitsText(data.units)}` : ''}`}
          </h1>
          <div style={{ color: '#5b6b73', marginTop: 4 }}>
            {`${total} photo${total === 1 ? '' : 's'} · link valid until ${dateEn(data.expiresAt)}`}
          </div>
        </div>
        {n > 0 && (
          <a href={publicReadyOrders.zipUrl(token)} download
            style={{ background: '#0f6e74', color: '#fff', fontWeight: 700, padding: '12px 18px', borderRadius: 8, textDecoration: 'none', minHeight: 44, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center' }}>
            ⬇ Download all (.zip)
          </a>
        )}
      </div>

      {n === 0 ? (
        <p style={{ color: '#5b6b73' }}>No photos for this order.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
          {photos.map((p, i) => (
            <button key={p.id} type="button" onClick={(e) => { retour.current = e.currentTarget; setOuverte(i); }}
              aria-label={`Open ${legende(p)} full screen`}
              style={{ padding: 0, border: '1px solid #cfd8dc', borderRadius: 10, overflow: 'hidden', background: '#e9eef0', cursor: 'zoom-in', display: 'flex', flexDirection: 'column', textAlign: 'left', font: 'inherit', color: 'inherit' }}>
              <img src={publicReadyOrders.photoUrl(token, p.id)} alt={legende(p)} loading="lazy"
                style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block', background: '#dfe6e9' }} />
              <span style={{ padding: '6px 8px', fontSize: 13, fontFamily: 'Consolas, "JetBrains Mono", monospace', background: '#fff', width: '100%', boxSizing: 'border-box' }}>
                {/* GS1 : la fin « …123456 » ; étiquette EffiTrace : entière (deux peuvent finir pareil). */}
                {legende(p)}<br />{p.sscc ? `SSCC ${abregeSscc(p.sscc)}` : 'SSCC —'}
              </span>
            </button>
          ))}
        </div>
      )}

      <p style={{ color: '#5b6b73', fontSize: 13, marginTop: 24 }}>
        Brain E-Log · Boulevard de la Technicité 1A, 7110 Houdeng-Goegnies, Belgium
      </p>

      {courante && (
        <div role="dialog" aria-modal="true" aria-label={legende(courante)}
          onClick={(e) => { if (e.target === e.currentTarget) fermerVue(); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,16,20,.92)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 12, boxSizing: 'border-box' }}>
          <img src={publicReadyOrders.photoUrl(token, courante.id)} alt={legende(courante)}
            style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 130px)', objectFit: 'contain', borderRadius: 6 }} />
          <div style={{ color: '#fff', marginTop: 10, textAlign: 'center', fontSize: 15, overflowWrap: 'anywhere' }}>
            {legende(courante)}{courante.sscc ? ` · SSCC ${courante.sscc}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {n > 1 && <button type="button" onClick={() => aller(-1)} style={btnVue} aria-label="Previous photo">←</button>}
            <button type="button" ref={fermer} onClick={fermerVue} style={btnVue}>✕ Close</button>
            {n > 1 && <button type="button" onClick={() => aller(1)} style={btnVue} aria-label="Next photo">→</button>}
          </div>
        </div>
      )}
    </div>
  );
}

const btnVue = { minHeight: 44, minWidth: 44, padding: '8px 14px', fontSize: 16, fontWeight: 700, borderRadius: 8, border: '1px solid #cfd8dc', background: '#fff', color: '#17232a', cursor: 'pointer' };
